import { env } from "~/env";
import { toCamelCase, toSnakeCaseObj, toSnakeCase } from "~/shared/lib/backend-mapper";

import {
    buildRequestHeaders,
    extractErrorCode,
    extractErrorMessage,
    mapStatusToCode,
    parseJsonResponse,
} from "./client-helpers";
import type { RequestContext } from "./client-types";

// The auth modules import this client, so they are pulled in on first use
// instead of at module load — otherwise the two form an import cycle.
function lazyModule<T>(load: () => Promise<T>): () => Promise<T> {
    let loaded: T | null = null;
    return async () => (loaded ??= await load());
}

const authClient = lazyModule(() => import("~/shared/lib/auth/auth-client"));
const authProvider = lazyModule(() => import("~/shared/lib/auth/auth-provider"));

async function getValidAccessToken(): Promise<string | null> {
    return (await authClient()).getValidAccessToken();
}

async function signOutOnForbidden(): Promise<void> {
    // Deliberately not awaited: the sign-out redirect must not hold up the
    // rejection of the request that discovered the dead session.
    void (await authProvider()).signOut();
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type { RequestContext };

/** Единая ошибка HTTP-клиента: `status`/`code`/`message` из ответа backend — на неё подписаны все UI-обработчики (toast, retry, redirect). */
export class ApiError extends Error {
    constructor(
        public status: number,
        public code: string,
        message: string,
    ) {
        super(message);
        this.name = "ApiError";
    }
}

// ---------------------------------------------------------------------------
// Base URLs
// ---------------------------------------------------------------------------

const API_URL = env.VITE_API_URL;
const BILLING_API_URL = env.VITE_BILLING_API_URL;

// ---------------------------------------------------------------------------
// Signout guard — prevent multiple signouts
// ---------------------------------------------------------------------------

let isSigningOut = false;

// 401 = session truly dead (invalid/expired JWT). Always logout.
// 403 = usually RBAC ("no access to this resource"), NOT a dead session —
// except from /v2/users/me, where backend uses 403 for BLOCKED/DELETED users.
function handleAuthError(status: number, path: string): void {
    const is401 = status === 401;
    const isBlockedUser = status === 403 && path.endsWith("/v2/users/me");
    if ((is401 || isBlockedUser) && !isSigningOut) {
        isSigningOut = true;
        void signOutOnForbidden();
    }
}

// ---------------------------------------------------------------------------
// Shared plumbing — every fetcher below goes through these three
// ---------------------------------------------------------------------------

/** Query params accepted by the GET helpers; keys are camelCase, converted on the way out. */
export type QueryParams = Record<string, string | number | boolean | undefined>;

/** Current access token, or `ApiError(401)` when the session cannot produce one. */
async function requireAccessToken(): Promise<string> {
    const token = await getValidAccessToken();
    if (!token) {
        throw new ApiError(401, "UNAUTHORIZED", "No valid token");
    }
    return token;
}

/**
 * Run a request and turn a non-OK response into `ApiError` — the single error
 * path for this module.
 *
 * One place decides all three things callers depend on: the machine-readable
 * `code` (from the body when the backend sent one), a human-readable `message`
 * (the backend's own `detail`/`message`, not the raw JSON), and whether a dead
 * session must sign the user out. Fetchers used to each answer these on their
 * own, which is how billing calls ended up neither logging out on 401 nor
 * showing a readable message.
 *
 * `authPath` is null for endpoints that carry no session (public billing) —
 * there is nothing to sign out of, and a 401 there is a server matter.
 */
async function fetchOrThrow(
    url: string,
    init: RequestInit | undefined,
    authPath: string | null,
): Promise<Response> {
    const response = await fetch(url, init);

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        const message = extractErrorMessage(errorText, `Backend API error (${response.status})`);
        if (authPath !== null) {
            handleAuthError(response.status, authPath);
        }
        throw new ApiError(response.status, extractErrorCode(errorText, response.status), message);
    }

    return response;
}

// ---------------------------------------------------------------------------
// Core fetch function
// ---------------------------------------------------------------------------

interface BaseFetchArgs {
    baseUrl: string;
    method: string;
    path: string;
    body?: unknown;
    context?: RequestContext;
}

async function baseFetch<T>({ baseUrl, method, path, body, context }: BaseFetchArgs): Promise<T> {
    const token = await requireAccessToken();

    const headers = buildRequestHeaders(token, context);

    const response = await fetchOrThrow(
        `${baseUrl}${path}`,
        {
            method,
            headers,
            ...(body != null && { body: JSON.stringify(toSnakeCaseObj(body)) }),
        },
        path,
    );

    return parseJsonResponse<T>(response);
}

/**
 * GET a paginated collection: body plus the row count from `Content-Range`.
 *
 * Separate from `baseFetch` because the total lives in a header, so the raw
 * `Response` is needed; shared by the ml-backend and billing variants, which
 * differ only in base URL.
 */
async function getJsonWithTotal<T>(
    baseUrl: string,
    path: string,
    params?: QueryParams,
): Promise<{ data: T; total: number }> {
    const token = await requireAccessToken();
    const response = await fetchOrThrow(
        `${baseUrl}${path}${buildQueryString(params)}`,
        { headers: buildRequestHeaders(token, undefined) },
        path,
    );

    const total = parseContentRange(response) ?? 0;
    const data = toCamelCase<T>(await response.json());
    return { data, total };
}

// ---------------------------------------------------------------------------
// Helper to build query string from params
// ---------------------------------------------------------------------------

/** Собрать query-string из camelCase-параметров (`undefined` пропускается), ключи переводятся в snake_case. */
export function buildQueryString(params?: QueryParams): string {
    if (!params) return "";
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) {
            searchParams.set(toSnakeCase(key), String(value));
        }
    }
    const qs = searchParams.toString();
    return qs ? `?${qs}` : "";
}

// ---------------------------------------------------------------------------
// ML Backend API helpers
// ---------------------------------------------------------------------------

/** GET-запрос к ml-backend с JWT-авторизацией; JSON-ответ маппится snake_case → camelCase. */
export async function apiGet<T>(path: string, params?: QueryParams, context?: RequestContext): Promise<T> {
    return baseFetch<T>({ baseUrl: API_URL, method: "GET", path: path + buildQueryString(params), context });
}

/** GET-запрос к ml-backend с общим числом строк из заголовка `Content-Range` (для пагинации). */
export async function apiGetWithTotal<T>(
    path: string,
    params?: QueryParams,
): Promise<{ data: T; total: number }> {
    return getJsonWithTotal<T>(API_URL, path, params);
}

/** POST-запрос к ml-backend с JWT-авторизацией; тело и ответ маппятся camelCase ↔ snake_case. */
export async function apiPost<T>(path: string, body?: unknown, context?: RequestContext): Promise<T> {
    return baseFetch<T>({ baseUrl: API_URL, method: "POST", path, body, context });
}

/** PATCH-запрос к ml-backend с JWT-авторизацией; тело и ответ маппятся camelCase ↔ snake_case. */
export async function apiPatch<T>(path: string, body?: unknown, context?: RequestContext): Promise<T> {
    return baseFetch<T>({ baseUrl: API_URL, method: "PATCH", path, body, context });
}

/** PUT-запрос к ml-backend с JWT-авторизацией; тело и ответ маппятся camelCase ↔ snake_case. */
export async function apiPut<T>(path: string, body?: unknown, context?: RequestContext): Promise<T> {
    return baseFetch<T>({ baseUrl: API_URL, method: "PUT", path, body, context });
}

/** DELETE-запрос к ml-backend с JWT-авторизацией (тело опционально); ответ маппится snake_case → camelCase. */
export async function apiDelete<T>(path: string, body?: unknown, context?: RequestContext): Promise<T> {
    return baseFetch<T>({ baseUrl: API_URL, method: "DELETE", path, body, context });
}

// ---------------------------------------------------------------------------
// V2 prefix helpers (routes mounted under /v2)
// ---------------------------------------------------------------------------

const V2_PREFIX = "/v2";

/** Как {@link apiGet}, но роут — под префиксом `/v2`. */
export function apiGetV2<T>(path: string, params?: QueryParams, context?: RequestContext): Promise<T> {
    return apiGet<T>(`${V2_PREFIX}${path}`, params, context);
}

/** Как {@link apiPost}, но роут — под префиксом `/v2`. */
export function apiPostV2<T>(path: string, body?: unknown, context?: RequestContext): Promise<T> {
    return apiPost<T>(`${V2_PREFIX}${path}`, body, context);
}

/** Как {@link apiPatch}, но роут — под префиксом `/v2`. */
export function apiPatchV2<T>(path: string, body?: unknown, context?: RequestContext): Promise<T> {
    return apiPatch<T>(`${V2_PREFIX}${path}`, body, context);
}

/** Как {@link apiDelete}, но роут — под префиксом `/v2`. */
export function apiDeleteV2<T>(path: string, body?: unknown, context?: RequestContext): Promise<T> {
    return apiDelete<T>(`${V2_PREFIX}${path}`, body, context);
}

// ---------------------------------------------------------------------------
// Billing API helpers
// ---------------------------------------------------------------------------

const BILLING_PREFIX = "/v2";

/** GET-запрос к billing API с JWT-авторизацией — роут собирается как `/v2{path}`. */
export async function billingGet<T>(path: string, params?: QueryParams): Promise<T> {
    return baseFetch<T>({
        baseUrl: BILLING_API_URL,
        method: "GET",
        path: `${BILLING_PREFIX}${path}` + buildQueryString(params),
    });
}

/** POST-запрос к billing API с JWT-авторизацией — роут собирается как `/v2{path}`. */
export async function billingPost<T>(path: string, body?: unknown): Promise<T> {
    return baseFetch<T>({ baseUrl: BILLING_API_URL, method: "POST", path: `${BILLING_PREFIX}${path}`, body });
}

/** PATCH-запрос к billing API с JWT-авторизацией — роут собирается как `/v2{path}`. */
export async function billingPatch<T>(path: string, body?: unknown): Promise<T> {
    return baseFetch<T>({
        baseUrl: BILLING_API_URL,
        method: "PATCH",
        path: `${BILLING_PREFIX}${path}`,
        body,
    });
}

/** DELETE-запрос к billing API с JWT-авторизацией — роут собирается как `/v2{path}`. */
export async function billingDelete<T = void>(path: string): Promise<T> {
    return baseFetch<T>({ baseUrl: BILLING_API_URL, method: "DELETE", path: `${BILLING_PREFIX}${path}` });
}

// ---------------------------------------------------------------------------
// Billing API — FormData upload (for CSV price upload)
// ---------------------------------------------------------------------------

// The browser must set `Content-Type` itself here (it appends the multipart
// boundary), so this one builds its headers instead of reusing the JSON set.
/** Загрузить `FormData` в billing API (`/v2{path}`, POST) — например, CSV с ценами; ответ маппится в camelCase. */
export async function billingPostForm<T>(path: string, formData: FormData): Promise<T> {
    const token = await requireAccessToken();
    const fullPath = `${BILLING_PREFIX}${path}`;

    const response = await fetchOrThrow(
        `${BILLING_API_URL}${fullPath}`,
        {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
        },
        fullPath,
    );

    const data: unknown = await response.json();
    return toCamelCase<T>(data);
}

// ---------------------------------------------------------------------------
// Content-Range header parser (for billing pagination)
// ---------------------------------------------------------------------------

/** Извлечь общее число строк из заголовка `Content-Range` billing-ответа (`items 0-9/42`) — `null`, если заголовка нет. */
export function parseContentRange(response: Response): number | null {
    const header = response.headers.get("content-range");
    if (!header) return null;
    // Format: "items 0-9/42" or "orders 0-9/42"
    const match = /\/(\d+)/.exec(header);
    return match ? parseInt(match[1]!, 10) : null;
}

// ---------------------------------------------------------------------------
// Raw fetch with Content-Range (for paginated billing endpoints)
// ---------------------------------------------------------------------------

/** GET-запрос к billing API с общим числом строк из `Content-Range` (для пагинации). */
export async function billingGetWithTotal<T>(
    path: string,
    params?: QueryParams,
): Promise<{ data: T; total: number }> {
    return getJsonWithTotal<T>(BILLING_API_URL, `${BILLING_PREFIX}${path}`, params);
}

// ---------------------------------------------------------------------------
// Billing API — public (no auth) for landing pricing block
// ---------------------------------------------------------------------------

/** Публичный (без JWT) GET к billing API — `/v2/billing/public{path}`, для блока цен на лендинге. */
export async function publicBillingGet<T>(path: string, params?: QueryParams): Promise<T> {
    const url = `${BILLING_API_URL}${BILLING_PREFIX}/billing/public${path}${buildQueryString(params)}`;
    // authPath null: no session behind these calls, so a 401 must not sign anyone out.
    const response = await fetchOrThrow(url, { headers: { "Content-Type": "application/json" } }, null);

    const data: unknown = await response.json();
    return toCamelCase<T>(data);
}

// ---------------------------------------------------------------------------
// Blob fetch — for file/image downloads (cross-origin OK)
// ---------------------------------------------------------------------------

// Centralised helper so all blob/file downloads route through one path that
// throws ApiError on !ok. Returns `headers` alongside the blob because some
// callers parse Content-Disposition for the server-suggested filename.
// Auth header / credentials policy is the caller's choice via `init`.
//
// `cache: "no-store"` by default: cross-origin media is first painted via a
// plain `<img>` (no `crossorigin`), which fetches WITHOUT an `Origin` header.
// Storage that reflects the request Origin then answers without an
// `Access-Control-Allow-Origin` header, and the browser caches that
// CORS-less response. A later `fetch()` of the same URL would reuse the
// poisoned cache entry and fail with "No 'Access-Control-Allow-Origin'".
// Bypassing the HTTP cache forces a fresh CORS request (with `Origin`), so the
// download succeeds. Callers may still override via `init.cache`.
/** Скачать blob (файл/изображение, в т.ч. cross-origin) — бросает `ApiError` при не-2xx, возвращает заголовки для чтения `Content-Disposition`. */
export async function fetchBlob(url: string, init?: RequestInit): Promise<{ blob: Blob; headers: Headers }> {
    const response = await fetch(url, { ...init, cache: init?.cache ?? "no-store" });
    if (!response.ok) {
        throw new ApiError(response.status, mapStatusToCode(response.status), `Failed to fetch blob: ${url}`);
    }
    return { blob: await response.blob(), headers: response.headers };
}
